import { PublicClientApplication, LogLevel } from "@azure/msal-browser";
import { debugLog } from "../Utils/DebugOverlay";

export const requiresInteraction = (errorMessage: string) => {
    if (!errorMessage || !errorMessage.length) {
        return false;
    }

    return (
        errorMessage.indexOf("consent_required") > -1 ||
        errorMessage.indexOf("interaction_required") > -1 ||
        errorMessage.indexOf("login_required") > -1
    );
};

export const fetchMsGraph = async (url: string, accessToken: string) => {
    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });

    return response.json();
};

export const GRAPH_SCOPES = {
    OPENID: "openid",
    PROFILE: "profile",
    USER_READ: "User.Read",
    MAIL_READ: "Mail.Read"
};

export const GRAPH_ENDPOINTS = {
    ME: "https://graph.microsoft.com/v1.0/me",
    MAIL: "https://graph.microsoft.com/v1.0/me/messages"
};

export const GRAPH_REQUESTS = {
    LOGIN: {
        scopes: [
            GRAPH_SCOPES.OPENID,
            GRAPH_SCOPES.PROFILE,
            GRAPH_SCOPES.USER_READ
        ]
    },
    EMAIL: {
        scopes: [GRAPH_SCOPES.MAIL_READ]
    }
};

export const msalApp = new PublicClientApplication({
    auth: {
        clientId: `${import.meta.env.VITE_AZURE_APPLICATION_ID}`,
        authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID}`,
        redirectUri: import.meta.env.VITE_REDIRECT_BASE_URI + "/auth",
        postLogoutRedirectUri: import.meta.env.VITE_REDIRECT_BASE_URI + "/home",
        navigateToLoginRequestUrl: true
    },
    cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
        claimsBasedCachingEnabled: true
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message) => {
                console.log(message);
            },
            logLevel: LogLevel.Warning,
            piiLoggingEnabled: false
        }
    }
});

export const loginScopes = [
    `api://${import.meta.env.VITE_AZURE_APPLICATION_ID}/Offers.View`
];

// Captured BEFORE initialize(), which re-mints the cookie. This is the only point at
// which its absence is observable, and it distinguishes "encryption key was lost" from
// "key survived but the cache went away for some other reason".
const hadEncryptionCookieAtLoad =
  typeof document !== 'undefined' && document.cookie.includes('msal.cache.encryption');

export const msalInitPromise = msalApp.initialize().then(() => {
  // MSAL v4 stores localStorage artifacts in plaintext (surviving process termination)
  // only when the id_token's signin_state claim contains 'kmsi' or 'dvc_dmjd'.
  // Note 'dvc_mngd' (Intune/Entra-joined) does NOT qualify.
  const signinState = (accounts: ReturnType<typeof msalApp.getAllAccounts>) =>
    (accounts[0]?.idTokenClaims as { signin_state?: string[] } | undefined)?.signin_state?.join(',') ?? '(none)';

  // Account count is the authoritative signal of cache loss: MSAL v4 encrypts its
  // localStorage artifacts with a key held in a session cookie and purges whatever it
  // cannot decrypt on init. (The cookie itself is re-minted here, so its presence
  // proves nothing.)
  debugLog('init:cacheState', () => ({
    accountCount: msalApp.getAllAccounts().length,
    hasTabPin: !!sessionStorage.getItem('tabAccountId'),
    enableSSO: localStorage.getItem('enableSSO') === 'true',
    hasLoginHint: !!localStorage.getItem('loginHint'),
    standalone: window.matchMedia?.('(display-mode: standalone)').matches ?? false,
    hadEncCookie: hadEncryptionCookieAtLoad,
    signinState: signinState(msalApp.getAllAccounts()),
  }));

  return msalApp.handleRedirectPromise().then((response) => {
    if (response?.account) {
      sessionStorage.setItem('tabAccountId', response.account.homeAccountId);
    }
    debugLog('init:handleRedirect', () => ({
      hasResponse: !!response,
      account: response?.account?.username ?? '(none)',
      accountCount: msalApp.getAllAccounts().length,
      signinState: response?.account
        ? signinState([response.account])
        : signinState(msalApp.getAllAccounts()),
    }));
    return response;
  }).catch((error) => {
    // Interactive auth outcomes from silent SSO attempts are expected — not a fatal init failure.
    const errorKey = error?.errorCode ?? error?.errorMessage ?? '';
    debugLog('init:handleRedirectError', () => ({
      errorCode: error?.errorCode ?? '(none)',
      needsInteraction: requiresInteraction(errorKey),
    }));
    if (requiresInteraction(errorKey)) return null;
    throw error;
  });
});

// Deferred promise resolved by AuthorizationProvider when MsalProvider finishes initialization
let resolveMsalReady = () => {};
export const msalReadyPromise = new Promise<void>((resolve) => {
  resolveMsalReady = resolve;
});
export function signalMsalReady() {
  resolveMsalReady();
}
